import { cashflowCharts } from "~/translations/cashflow/cashflowCharts";
import { openFinanceProviderToNativeLanguageName } from "~/components/product/openfinance/openfinanceProviders";
import { LANGUAGES, type Language } from "~/utils/language";
import type { Fiat, TransactionRule } from "~/types/global.types";
import type {
  BudgetCategory,
  BudgetCategoryGroup,
  BudgetCategoryCodeMapping,
  ManualTransactionSource,
} from "~/types/budget.types";
import { cashflowDateType, transacitonActionType } from "./cashflowEnums";
import {
  convertCurrencyOnDate,
  type FiatRateHistory,
} from "~/calculations/genericCalculations/convertCurrencyOnDate";
import { stringToColor } from "./transactionSummaryChart";
import {
  transactionMatchesRule,
  computeChecksum,
  orderRulesForApplication,
} from "~/utils/transactionRuleMatcher";
import {
  applyTransactionEditPayload,
  parseTransactionEditRulePayload,
  type TransactionEditRulePayload,
} from "~/utils/transactionEditRule";
import { MANUAL_ACCOUNT_PREFIX } from "~/utils/manualAccountSelection";

export interface monthOption {
  value: Date;
  label: string;
}

export function getMonthsFromTransactions(
  lang: Language,
  dateRefFunctionType: string,
  transactions: Transaction[]
): monthOption[] {
  let months: monthOption[] = [];
  // Copy first — sorting the caller's array mutates React state mid-render.
  let sortedtrasactionsbyDate = [...transactions].sort((a, b) => {
    let dateA = getDateByFuncType(dateRefFunctionType, a);
    let dateB = getDateByFuncType(dateRefFunctionType, b);
    return dateB.getTime() - dateA.getTime();
  });
  for (const transaction of sortedtrasactionsbyDate) {
    let localMonthName = getlocalMonthName(
      lang,
      getDateByFuncType(dateRefFunctionType, transaction)
    );
    //find month in months from local month name
    if (
      !months.find((month) => month.label === localMonthName) &&
      localMonthName !== ""
    ) {
      months.push({
        label: localMonthName,
        value: getMonthDate(
          getDateByFuncType(dateRefFunctionType, transaction)
        ),
      });
    }
  }

  months.unshift({
    label: cashflowCharts[lang].Months.All,
    value: new Date(
      sortedtrasactionsbyDate[
        sortedtrasactionsbyDate.length - 1
      ]?.date.valueDate
    ),
  });
  months.unshift({ label: "Custom Date", value: new Date() });
  return months;
}

export function getDateByFuncType(
  type: string,
  transaction: Transaction
): Date {
  let dateObj = transaction.date;
  switch (type) {
    case cashflowDateType.TRANSACTION_DATE:
      return getDateFromTransacitonDate(transaction);
    case cashflowDateType.VALUE_DATE:
      return getDateFromValueDate(dateObj);
    default:
      return getDateFromValueDate(dateObj);
  }
}

export function getDateFromTransacitonDate(transaction: Transaction): Date {
  let dateObj = transaction.date;
  // Hebrew payment keywords for matching against Israeli bank transaction data
  const paymentStrings = ["תשלום", "תשלומים", "מתוך"]; // ["payment", "payments", "out of"]
  for (const paymentString of paymentStrings) {
    if (
      (transaction.transactionDescription &&
        transaction.transactionDescription.indexOf(paymentString) > -1) ||
      (transaction.details && transaction.details.indexOf(paymentString) > -1)
    ) {
      return getDateFromValueDate(dateObj);
    }
  }
  if (dateObj) {
    let date = dateObj.valueDate;
    if (dateObj.transactionDate) {
      date = dateObj.transactionDate;
    }
    return new Date(date);
  }
  return new Date();
}

export function getDateFromValueDate(dateObj: {
  valueDate: string;
  bookingDate: string;
  transactionDate: string;
}): Date {
  if (dateObj) {
    let date = dateObj.valueDate;
    return new Date(date);
  }
  return new Date();
}

export function getMonthDate(date: Date): Date {
  let month = date.getMonth();
  let year = date.getFullYear();
  let monthStr = month < 9 ? `0${month + 1}` : `${month + 1}`;
  //return the first day of the month as a date object
  return new Date(`${year}-${monthStr}-01T00:00:00.000Z`);
}

export function getlocalMonthName(lang: Language, date: Date): string {
  let month = date.getMonth();
  let year = date.getFullYear();
  if (translateMonth(lang, month) === "" || !date) {
    return "";
  }
  return `${translateMonth(lang, month)}-${year}`;
}

export function getlocalMonthNameWithCycleDay(
  lang: Language,
  functionType: string,
  cycleDay: number,
  transaction: Transaction
): string {
  let date = getDateByFuncType(functionType, transaction);
  let day = date.getDate();
  let month = date.getMonth();
  let year = date.getFullYear();
  if (day <= cycleDay) {
    month = month - 1;
    if (month === -1) {
      month = 11;
      year = year - 1;
    }
  }
  if (translateMonth(lang, month) === "" || !date) {
    return "";
  }
  return `${translateMonth(lang, month)}-${year}`;
}

export function translateMonth(lang: Language, month: number): string {
  switch (month + 1) {
    case 1:
      return cashflowCharts[lang].Months.January;
    case 2:
      return cashflowCharts[lang].Months.February;
    case 3:
      return cashflowCharts[lang].Months.March;
    case 4:
      return cashflowCharts[lang].Months.April;
    case 5:
      return cashflowCharts[lang].Months.May;
    case 6:
      return cashflowCharts[lang].Months.June;
    case 7:
      return cashflowCharts[lang].Months.July;
    case 8:
      return cashflowCharts[lang].Months.August;
    case 9:
      return cashflowCharts[lang].Months.September;
    case 10:
      return cashflowCharts[lang].Months.October;
    case 11:
      return cashflowCharts[lang].Months.November;
    case 12:
      return cashflowCharts[lang].Months.December;
    default:
      return "";
  }
}

export function generateCategoryName(
  lang: Language,
  transaction: Transaction
): string {
  let categoryName = transaction.calculatedCategory || transaction.category.sub;
  let translatedName =
    translateCategoryName(lang, categoryName) ||
    categoryName ||
    cashflowCharts[lang].Categories.Uncategorized;
  return translatedName;
}

export type CategoryData = {
  label: string;
  value: string;
  color: string;
};

export type TransactionWithCategory = Transaction & {
  categoryName: string;
  color: string;
  valueString: string;
  transactionDescription: string;
  budgetGroupName?: string;
};

export interface Transaction {
  id: string;
  SK: string;
  userId: string;
  connectionId: string;
  accountId: string;
  providerId: string;
  accountNumber: string;
  status: string;
  amount: {
    originalAmount: {
      amount: string;
      currency: string;
    };
    chargedAmount: {
      amount: string;
      currency: string;
    };
  };
  description: {
    additionalInfo: any;
    description: string;
    fixedText: string;
  };
  category: {
    categorizedBy: string;
    sub: string;
    main: string;
  };
  /** User override from Open Finance; takes precedence over `category` for display/aggregation when set */
  changedCategory?: {
    sub: string;
    main: string;
  };
  installments: {};
  type: string;
  date: {
    valueDate: string;
    bookingDate: string;
    transactionDate: string;
  };
  createdAt: string;
  updatedAt: string;

  //following are enrichemnt fields
  calculatedCategory: string;
  ignored: boolean;
  isInvestment: boolean;
  details: string;
  transactionDescription: string;
  rules: TransactionRule[];
  isDuplicate: boolean;
  autoDuplicate?: boolean;
  /** Same checksum as an earlier row in this list (2nd+ occurrence in group) */
  duplicateInChecksumGroup?: boolean;
  /** User confirmed this checksum group as intentional duplicates; counted in cash flow */
  duplicateConfirmedAsReal?: boolean;
  connectedCard?: string;
  disableActions?: boolean;
  checksum?: string;
  /** Explicit budget category from a manual tx/rule (or EDIT_TRANSACTION). */
  budgetCategoryId?: string | null;
  budgetCategoryColor?: string;
  budgetCategoryEmoji?: string;
  budgetSubCategoryName?: string;
  transactionEditDefaults?: TransactionEditRulePayload;
  merchantName?: string;
  merchantAddress?: {
    country?: string;
    townName?: string;
  };
}

type CategoryLabelKey = keyof typeof cashflowCharts["HEB"]["Categories"];

/**
 * Canonical sub-category code → translation label key. Single source of truth
 * for both forward (code → localized label) and reverse (localized label →
 * canonical code) lookups.
 *
 * When several codes share the same label (e.g. LOANS/LOAN, FINANCE_OTHER/
 * FORECLOSURE), the first entry wins as the canonical code for the reverse map,
 * so list the primary code before its aliases.
 */
const CATEGORY_CODE_TO_LABEL_KEY: Record<string, CategoryLabelKey> = {
  UNCATEGORIZED: "Uncategorized",
  BUSINESS_EXPENSES: "BusinessExpenses",
  CHECKS_GENERAL: "ChecksGeneral",
  CREDIT_CARD_EXPENSES: "CreditCardExpenses",
  INTEREST_RATES: "InterestRates",
  INTEREST_RATE: "InterestRates",
  CASH_WITHDRAWALS: "CashWithdrawls",
  FINANCE_OTHER: "FinanceOther",
  FORECLOSURE: "FinanceOther",
  HEALTHCARE: "Healthcare",
  "INSURANCE_&_FEES": "InsuranceAndFees",
  SAVINGS: "Saving",
  HOME_IMPROVEMENTS_OTHER: "HomeImprovementsOther",
  OTHER: "Other",
  REIMBURSEMENTS_OTHER: "ReimbursementsOther",
  SERVICES: "Services",
  BIT_PAYBOX: "Services",
  FEES: "Fees",
  LOANS: "Loans",
  LOAN: "Loans",
  BENEFITS_OTHER: "BenefitsOthers",
  CAPITAL_MARKET: "CapitalMarket",
  "BOOKS_&_GAMES": "BooksAndGames",
  GROCERIES: "Groceries",
  SHOPPING_OTHER: "ShoppingOther",
  "SPORTS_&_FITNESS": "SportsAndFitness",
  RESTAURANT: "Restaurant",
  "CLOTHES_&_ACCESSORIES": "ClothesAndAccessories",
  "COFFEE_&_SNACKS": "CoffeeAndSnacks",
  ELECTRONICS: "Electronics",
  PHARMACY: "Pharmacy",
  "CAR_&_FUEL": "CarAndFuel",
  COMMUNICATIONS: "Communications",
  TRANSPORT_OTHER: "TransportOther",
  EDUCATION: "Education",
  "HOBBY_&_SPORTS_EQUIPMENT": "HobbyAndSportsEquipment",
  GOVERNMENT_SERVICES: "GovernmentServices",
  "GOVERNMENT SERVICES": "GovernmentServices",
  "ALCOHOL_&_TOBACCO": "AlcoholAndTobacco",
  "CULTURE_&_EVENTS": "CultureAndEvents",
  VACATION: "Vacation",
  CHARITY: "Charity",
  PUBLIC_TRANSPORT: "PublicTransport",
  BARS: "Bars",
  GIFTS: "Gifts",
  "RENOVATION_&_REPAIRS": "RenovationAndRepairs",
  FLIGHTS: "Flights",
  BEAUTY: "Beauty",
  TAXI: "Taxi",
  "FOOD_&_DRINKS_OTHER": "FoodAndDrinksOther",
  LEISURE_OTHER: "LeisureOther",
  FINANCE: "Finance",
  "FOOD_&_DRINKS": "FoodAndDrinks",
  LEISURE: "Leisure",
  SHOPPING: "Shopping",
  TRANSPORT: "Transport",
  "HOUSEHOLD_&_SERVICES": "HouseholdAndServices",
  "HEALTH_&_BEAUTY": "HealthAndBeauty",
  "HEALTH_&_BEAUTY_OTHER": "HealthAndBeautyOther",
  HEALTH_AND_BEAUTY_OTHER: "HealthAndBeautyOther",
  HOME_IMPROVEMENTS: "HomeImprovements",
  REIMBURSEMENTS: "Reimbursements",
  BENEFITS: "Benefits",
  SALARY: "Salary",
  UTILITIES: "Utilities",
  MORTGAGE: "Mortgage",
  HOME: "Home",
  RENT: "Rent",
  GARDEN: "Garden",
  "FURNITURE_&_INTERIOR": "FornitureAndInterior",
  "FORNITURE_&_INTERIOR": "FornitureAndInterior",
  HOBBIES: "Hobbies",
  PETS: "Pets",
  KIDS: "Kids",
  BUSINESS_VENDOR: "BusinessVendor",
  SALARY_OTHER: "SalaryOther",
  EYECARE: "EyeCare",
  DIRECT_DEBIT: "DirectDebit",
  BANK_TRANSFER: "BankTransfer",
  CREDIT_CARD_CHECKING: "CreditCardChecking",
  CreditCardChecking: "CreditCardChecking",
  creditCardChecking: "CreditCardChecking",
  FUNDS_NOT_COVER: "FundsNotCover",
  INCOMES_EXPENSES: "IncomesExpenses",
  NATIONAL_INSURANCE: "NationalInsurance",
  NOT_RESERVE_DUTY: "NotReserveDuty",
  VAT: "VAT",
  RETURN: "Return",
  ONLY_RETURN: "OnlyReturn",
  REFUND: "Refund",
  CANCELLED: "Cancelled",
  CHQ_RETURN: "ChqReturn",
  HOK_RETURN: "HokReturn",
  NEGATIVE_BEHAVIOR: "NegativeBehavior",
  RESTRICTED: "NegativeBehavior",
  ASSETS: "Assets",
  INVESTMENTS: "Investments",
  SECURITIES: "Securities",
  TRADING: "Trading",
  PENSION: "Pension",
  RECENT_PENSION: "RecentPension",
  LOAN_TRANSACTION: "LoanTransaction",
  LOAN_OVERDUE: "LoanOverdue",
  OVERDUE_SETTLEMENT: "OverdueSettlement",
  MINISTRY_OF_CONSTRECTION: "MinistryOfConstrection",
  GOV_EXPENSES: "GovExpenses",
  DEPOSIT: "Deposit",
  TRANSFER: "Transfer",
  INSURANCE: "Insurance",
  COMMISSION: "Commission",
  CHQ_COMMISSION: "ChqCommission",
  HOK_COMMISSION: "HokCommission",
  HOK_AKAM_COMMISSION: "HokAkamCommission",
  OVERDUE_RELATED_COMMISSION: "OverdueRelatedCommission",
  CASH_INCOME: "CashIncome",
  ALIMONY: "Alimony",
  FOREIGN_EXCHANGE: "ForeignExchange",
  CHQ_INCOME: "ChqIncome",
  HEALTH_FUND: "HealthFund",
  BILLS: "Bills",
  TECHNI: "Techni",
  ALERT_NOTICE: "AlertNotice",
  REGISTERED_MAIL: "RegisteredMail",
  NON_REVENUE: "NonRevenue",
  NOT_CLASSIFIED: "NotClassified",
  RESERVE_DUTY: "ReserveDuty",
};

export function translateCategoryName(
  lang: Language,
  categoryName: string
): string {
  const key = CATEGORY_CODE_TO_LABEL_KEY[categoryName];
  return key ? cashflowCharts[lang].Categories[key] : categoryName;
}

/**
 * Reverse of {@link translateCategoryName}: maps a localized category label (in
 * any supported language) back to its canonical English code. Built lazily from
 * {@link CATEGORY_CODE_TO_LABEL_KEY}; the first code listed for a given label
 * wins. Returns `null` when the label is not a recognized category label (e.g.
 * a user's free-text custom category).
 */
let cachedLabelToCodeMap: Map<string, string> | null = null;

function getLabelToCanonicalCodeMap(): Map<string, string> {
  if (cachedLabelToCodeMap) return cachedLabelToCodeMap;
  const map = new Map<string, string>();
  for (const [code, key] of Object.entries(CATEGORY_CODE_TO_LABEL_KEY)) {
    for (const lng of LANGUAGES) {
      const label = cashflowCharts[lng].Categories[key];
      if (!label) continue;
      const existing = map.get(label);
      if (existing === undefined) {
        map.set(label, code);
        continue;
      }
      // When several codes share a label, prefer the "main" category over its
      // `_OTHER` sub-bucket (e.g. FOOD_&_DRINKS over FOOD_&_DRINKS_OTHER) so the
      // canonical code lines up with the primary code variant.
      if (existing.endsWith("_OTHER") && !code.endsWith("_OTHER")) {
        map.set(label, code);
      }
    }
  }
  cachedLabelToCodeMap = map;
  return map;
}

/**
 * Coerce a creatable-select value/label to text. Expense-chart rows leak a
 * numeric `value` (the amount) into the rule form; calling `.trim()` on that
 * number crashes the cashflow page when adding a new category.
 */
export function categoryOptionText(value: unknown): string {
  if (value == null) return "";
  return String(value).trim();
}

/** Chart totals use numeric `value`. The rule form needs the category name. */
export function categoryOptionsFromExpenseChart(
  rows: Array<{ name?: string; label?: string; color?: string }>
): CategoryData[] {
  const seen = new Set<string>();
  const options: CategoryData[] = [];
  for (const row of rows) {
    const label = categoryOptionText(row.label || row.name);
    if (!label) continue;
    const key = label.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({
      label,
      value: label,
      color: typeof row.color === "string" ? row.color : "",
    });
  }
  return options;
}

export function getCanonicalCategoryCodeForLabel(label: string): string | null {
  const trimmed = categoryOptionText(label);
  if (!trimmed) return null;
  return getLabelToCanonicalCodeMap().get(trimmed) ?? null;
}

/**
 * Persist and look up CATEGORY_CHANGE values as Open Finance codes when the
 * user picked/typed a localized label (e.g. "תחבורה" → TRANSPORT). Custom
 * free-text names are left unchanged.
 */
export function canonicalizeCategoryChangeValue(
  value: string | number | null | undefined
): string {
  const trimmed = categoryOptionText(value);
  if (!trimmed) return "";
  return getCanonicalCategoryCodeForLabel(trimmed) || trimmed;
}

/** Reject leaked JSON, UUIDs, and bare amounts from the creatable category box. */
export function isTypedCategoryName(
  value: string | number | null | undefined
): boolean {
  const trimmed = categoryOptionText(value);
  if (trimmed.length < 2) return false;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return false;
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      trimmed
    )
  ) {
    return false;
  }
  if (/^\d+([.,]\d+)?$/.test(trimmed)) return false;
  return true;
}

export function generateNewRuleId(rules: TransactionRule[]) {
  let maxId = 0;
  rules.forEach((rule) => {
    if (rule.id && rule.id > maxId) {
      maxId = rule.id;
    }
  });
  return maxId + 1;
}

export function calculateFilteredIgnoredTransactions(
  lang: Language,
  transactions: Transaction[],
  selectedAccounts: OptionInterface[],
  transactionRules: TransactionRule[]
): Transaction[] {
  let filteredTransactions = transactions.filter((transaction) => {
    return selectedAccounts.find(
      (account) => account.value === transaction.accountNumber
    );
  });
  filteredTransactions.forEach((transaction) => {
    transaction.ignored = false;
    transaction.isInvestment = false;
    transaction.rules = [];
    const matchingRules = orderRulesForApplication(
      transactionRules.filter((rule) =>
        transactionMatchesRule(rule, transaction)
      )
    );
    matchingRules.forEach((rule) =>
      applyRuleToTransaction(lang, rule, transaction)
    );
  });
  return filteredTransactions;
}

export function applyRuleToTransactions(
  lang: Language,
  transactions: Transaction[],
  rule: TransactionRule
) {
  transactions.forEach((transaction) => {
    if (transactionMatchesRule(rule, transaction)) {
      applyRuleToTransaction(lang, rule, transaction);
    }
  });
}

export function removeRuleFromTransactions(
  lang: Language,
  transactions: Transaction[],
  expensesCategoryType: string,
  incomeCategoryType: string,
  rule: TransactionRule,
  transactionRules: TransactionRule[],
  budgetMap?: BudgetCategoryMap
) {
  transactions.forEach((transaction) => {
    const idx = (transaction.rules ?? []).findIndex((r) => r.id === rule.id);
    if (idx !== -1) {
      transaction.ignored = false;
      transaction.isInvestment = false;
      calculateTransactionsCategory(
        lang,
        transaction,
        expensesCategoryType,
        incomeCategoryType
      );
      transaction.rules = [];
      orderRulesForApplication(transactionRules).forEach((remaining) => {
        if (
          remaining.id !== rule.id &&
          transactionMatchesRule(remaining, transaction)
        ) {
          applyRuleToTransaction(lang, remaining, transaction);
        }
      });
      if (budgetMap) {
        const amount = parseFloat(transaction.amount.chargedAmount.amount);
        const shouldResolve =
          amount < 0
            ? expensesCategoryType !== cashflowCharts[lang].CategoryTypes.Sub
            : incomeCategoryType !== cashflowCharts[lang].CategoryTypes.Sub;
        if (shouldResolve) {
          transaction.budgetSubCategoryName = transaction.calculatedCategory;
          const resolved = resolveBudgetCategory(transaction, budgetMap);
          transaction.calculatedCategory = resolved.name;
          transaction.budgetCategoryColor = resolved.color;
          transaction.budgetCategoryEmoji = resolved.emoji;
        }
      }
    }
  });
}

export function addCategoryAndValue(
  lang: Language,
  transactions: Transaction[],
  currencySymbol?: string
) {
  const transactionsWithCategory = transactions.map((transaction) => {
    const checksum = transaction.checksum || computeChecksum(transaction);
    const budgetGroupName = transaction.budgetSubCategoryName
      ? generateCategoryName(lang, transaction)
      : undefined;
    let categoryName = transaction.budgetSubCategoryName
      ? translateCategoryName(lang, transaction.budgetSubCategoryName) ||
        transaction.budgetSubCategoryName
      : generateCategoryName(lang, transaction);
    let color = transaction.budgetCategoryColor || stringToColor(categoryName);
    if (transaction.ignored) {
      color = "#E2E8F0";
    }
    let valueString = currencySymbol
      ? generateTransactionValueString(transaction, currencySymbol)
      : generateTransactionValueString(transaction);
    return {
      ...transaction,
      categoryName,
      color,
      valueString,
      checksum,
      budgetGroupName,
    };
  }, []);
  return transactionsWithCategory;
}

function formatTransactionMoneyAmount(
  amount: number,
  currency: string | undefined,
  maximumFractionDigits: number,
  fallbackSymbol = ""
): string {
  const abs = Math.abs(amount);
  const code = String(currency ?? "")
    .trim()
    .toUpperCase();
  if (!code) {
    return fallbackSymbol
      ? `${abs.toLocaleString("he-IL")} ${fallbackSymbol}`
      : abs.toLocaleString("he-IL");
  }
  try {
    return abs.toLocaleString("he-IL", {
      style: "currency",
      currency: code,
      maximumFractionDigits,
      minimumFractionDigits: 0,
    });
  } catch {
    return `${abs.toLocaleString("he-IL")} ${code}`;
  }
}

export function generateTransactionValueString(
  transaction: Transaction,
  currencySymbol = "₪"
) {
  let value = Math.abs(parseFloat(transaction.amount?.chargedAmount?.amount));

  let valueString = value
    ? formatTransactionMoneyAmount(
        value,
        transaction.amount?.chargedAmount?.currency,
        0,
        currencySymbol
      )
    : 0 + " " + currencySymbol;
  return valueString;
}

/** Original FX amount when it differs from the charged currency (e.g. ₪ charge, €/CZK source). */
export function getTransactionOriginalAmountLabel(
  transaction: Transaction
): string | null {
  const original = transaction.amount?.originalAmount;
  const charged = transaction.amount?.chargedAmount;
  const originalCurrency = String(original?.currency ?? "")
    .trim()
    .toUpperCase();
  const chargedCurrency = String(charged?.currency ?? "")
    .trim()
    .toUpperCase();
  if (!originalCurrency || originalCurrency === chargedCurrency) {
    return null;
  }
  const value = parseFloat(String(original?.amount ?? ""));
  if (!Number.isFinite(value) || value === 0) {
    return null;
  }
  return formatTransactionMoneyAmount(value, originalCurrency, 2);
}

export function applyConnectedCardFilter(
  transactions: Transaction[],
  selectedAccounts: OptionInterface[]
) {
  const cardNumbersList = selectedAccounts.map((account) => account.value);
  transactions.forEach((transaction) => {
    if (
      transaction.connectedCard &&
      cardNumbersList.includes(transaction.connectedCard)
    ) {
      transaction.ignored = true;
      transaction.disableActions = true;
    } else if (
      transaction.ignored &&
      transaction?.disableActions &&
      !(transaction.rules ?? []).some(
        (r) => r.action_type === transacitonActionType.IGNORE
      )
    ) {
      transaction.disableActions = false;
      transaction.ignored = false;
    } else transaction.disableActions = false;
  });
}

/**
 * After rules are applied, mark 2nd+ rows per checksum as duplicate peers;
 * `autoDuplicate` excludes from totals unless CONFIRM_DUPLICATE exists for that checksum.
 */
export function applyDuplicateGroupFlags(
  transactions: Transaction[],
  transactionRules: TransactionRule[]
) {
  const confirmedChecksums = new Set(
    transactionRules
      .filter((r) => r.action_type === transacitonActionType.CONFIRM_DUPLICATE)
      .map((r) => r.rule_value)
  );

  for (const tx of transactions) {
    tx.autoDuplicate = false;
    tx.duplicateInChecksumGroup = false;
    tx.duplicateConfirmedAsReal = false;
    tx.checksum = tx.checksum || computeChecksum(tx);
  }

  const checksumGroups = new Map<string, Transaction[]>();
  for (const tx of transactions) {
    if (!tx.checksum) continue;
    const group = checksumGroups.get(tx.checksum);
    if (group) group.push(tx);
    else checksumGroups.set(tx.checksum, [tx]);
  }

  for (const [checksum, group] of checksumGroups) {
    if (group.length <= 1) continue;
    const confirmed = confirmedChecksums.has(checksum);
    for (let i = 1; i < group.length; i++) {
      group[i].duplicateInChecksumGroup = true;
      if (confirmed) {
        group[i].duplicateConfirmedAsReal = true;
      } else {
        group[i].autoDuplicate = true;
      }
    }
  }
}

export interface BudgetCategoryMapEntry {
  name: string;
  color: string;
  emoji?: string;
}

export interface BudgetCategoryMap {
  spending: Map<string, BudgetCategoryMapEntry>;
  income: Map<string, BudgetCategoryMapEntry>;
  /** Lookup by budget category id (for manual txs with an explicit assignment). */
  byId: Map<string, BudgetCategoryMapEntry>;
  /** Lookup by localized / English budget category name (e.g. "תחבורה"). */
  byName: Map<string, BudgetCategoryMapEntry>;
  fallbackSpending: BudgetCategoryMapEntry;
  fallbackIncome: BudgetCategoryMapEntry;
  investmentSpending?: BudgetCategoryMapEntry;
  investmentIncome?: BudgetCategoryMapEntry;
}

/**
 * Build direction-aware maps from Open Finance subcategory codes to budget
 * category names + colors. Resolution: user code mappings > auto_assign > fallback.
 */
export function buildBudgetCategoryMap(
  lang: Language,
  groups: BudgetCategoryGroup[],
  categories: BudgetCategory[],
  userMappings: BudgetCategoryCodeMapping[]
): BudgetCategoryMap {
  const incomeGroupIds = new Set(
    groups.filter((g) => g.group_type === "income").map((g) => g.id)
  );

  const catById = new Map<string, BudgetCategory>();
  for (const cat of categories) catById.set(cat.id, cat);

  const nameForCat = (cat: BudgetCategory): string =>
    lang === "ENG" ? cat.name_en || cat.name : cat.name;

  const entryForCat = (cat: BudgetCategory): BudgetCategoryMapEntry => ({
    name: nameForCat(cat),
    color: cat.color || stringToColor(nameForCat(cat)),
    emoji: cat.emoji || undefined,
  });

  const spendingMap = new Map<string, BudgetCategoryMapEntry>();
  const incomeMap = new Map<string, BudgetCategoryMapEntry>();
  const byId = new Map<string, BudgetCategoryMapEntry>();
  const byName = new Map<string, BudgetCategoryMapEntry>();

  for (const cat of categories) {
    if (cat.is_hidden) continue;
    const entry = entryForCat(cat);
    byId.set(cat.id, entry);
    byName.set(cat.name.trim().toLocaleLowerCase(), entry);
    if (cat.name_en?.trim()) {
      byName.set(cat.name_en.trim().toLocaleLowerCase(), entry);
    }
    const codes = cat.auto_assign_category_codes;
    if (!codes?.length) continue;
    const isIncome = incomeGroupIds.has(cat.group_id);
    const target = isIncome ? incomeMap : spendingMap;
    for (const code of codes) {
      if (!target.has(code)) target.set(code, entry);
    }
  }

  for (const m of userMappings) {
    const cat = catById.get(m.budget_category_id);
    if (!cat) continue;
    const entry = entryForCat(cat);
    if (m.direction === "income") {
      incomeMap.set(m.code, entry);
    } else {
      spendingMap.set(m.code, entry);
    }
  }

  const defaultOtherName = lang === "ENG" ? "Other" : "אחר";
  const defaultOtherIncomeName =
    lang === "ENG" ? "Other Income" : "הכנסות אחרות";

  let fallbackSpending: BudgetCategoryMapEntry = {
    name: defaultOtherName,
    color: "#636167",
  };
  let fallbackIncome: BudgetCategoryMapEntry = {
    name: defaultOtherIncomeName,
    color: "#5a5888",
  };

  for (const cat of categories) {
    if (incomeGroupIds.has(cat.group_id)) {
      if (cat.name === "הכנסות אחרות" || cat.name_en === "Other Income") {
        fallbackIncome = entryForCat(cat);
      }
    } else {
      if (cat.name === "שונות" || cat.name_en === "Other") {
        fallbackSpending = entryForCat(cat);
      }
    }
  }

  let investmentSpending: BudgetCategoryMapEntry | undefined;
  let investmentIncome: BudgetCategoryMapEntry | undefined;
  for (const cat of categories) {
    if (
      cat.name === "השקעות" ||
      cat.name_en === "Investment" ||
      cat.name_en === "Investments"
    ) {
      if (incomeGroupIds.has(cat.group_id)) {
        investmentIncome = entryForCat(cat);
      } else {
        investmentSpending = entryForCat(cat);
      }
    }
  }

  return {
    spending: spendingMap,
    income: incomeMap,
    byId,
    byName,
    fallbackSpending,
    fallbackIncome,
    investmentSpending,
    investmentIncome,
  };
}

function lookupBudgetCategoryForCode(
  code: string | null | undefined,
  map: Map<string, BudgetCategoryMapEntry>,
  byName: Map<string, BudgetCategoryMapEntry>,
  fallback: BudgetCategoryMapEntry
): BudgetCategoryMapEntry {
  if (!code) return fallback;
  const trimmed = code.trim();
  const canonical = canonicalizeCategoryChangeValue(trimmed);
  return (
    map.get(canonical) ||
    map.get(trimmed) ||
    byName.get(trimmed.toLocaleLowerCase()) ||
    byName.get(canonical.toLocaleLowerCase()) ||
    fallback
  );
}

function resolveBudgetCategory(
  transaction: Transaction,
  budgetMap: BudgetCategoryMap
): BudgetCategoryMapEntry {
  if (transaction.budgetCategoryId) {
    const explicit = budgetMap.byId.get(transaction.budgetCategoryId);
    if (explicit) return explicit;
  }
  // Rules are applied last-write-wins. A later checksum override must beat
  // an earlier wider category rule, including in budget mode.
  const categoryRules = (transaction.rules ?? []).filter(
    (r) => r.action_type === transacitonActionType.CATEGORY_CHANGE
  );
  const categoryChangeRule = categoryRules[categoryRules.length - 1];
  const effectiveCode =
    categoryChangeRule?.action_value ||
    transaction.changedCategory?.sub ||
    transaction.category.sub;
  const amount = parseFloat(transaction.amount.chargedAmount.amount);
  if (amount < 0) {
    return lookupBudgetCategoryForCode(
      effectiveCode,
      budgetMap.spending,
      budgetMap.byName,
      budgetMap.fallbackSpending
    );
  }
  return lookupBudgetCategoryForCode(
    effectiveCode,
    budgetMap.income,
    budgetMap.byName,
    budgetMap.fallbackIncome
  );
}

export function enrichTransactions(
  lang: Language,
  transactions: Transaction[],
  expensesCategoryType: string,
  incomeCategoryType: string,
  transactionRules: TransactionRule[],
  budgetMap?: BudgetCategoryMap
) {
  transactions.forEach((transaction) => {
    transaction.ignored = false;
    transaction.isInvestment = false;
    transaction.autoDuplicate = false;
    transaction.duplicateInChecksumGroup = false;
    transaction.duplicateConfirmedAsReal = false;
    transaction.checksum = transaction.checksum || computeChecksum(transaction);
    transaction.budgetCategoryColor = undefined;
    transaction.rules = [];
    calculateTransactionsCategory(
      lang,
      transaction,
      expensesCategoryType,
      incomeCategoryType
    );
    addTransactionDescription(transaction);
    const matchingRules = orderRulesForApplication(
      transactionRules.filter((rule) =>
        transactionMatchesRule(rule, transaction)
      )
    );
    matchingRules.forEach((rule) =>
      applyRuleToTransaction(lang, rule, transaction)
    );
  });

  // When in budget mode, re-resolve every transaction through the budget map.
  // This ensures CATEGORY_CHANGE rules don't create separate slices — all
  // transactions are grouped by their budget category based on the underlying
  // Open Finance sub-code.
  if (budgetMap) {
    const isExpensesBudget =
      expensesCategoryType !== cashflowCharts[lang].CategoryTypes.Sub;
    const isIncomeBudget =
      incomeCategoryType !== cashflowCharts[lang].CategoryTypes.Sub;
    for (const transaction of transactions) {
      const amount = parseFloat(transaction.amount.chargedAmount.amount);
      const shouldResolve = amount < 0 ? isExpensesBudget : isIncomeBudget;
      if (shouldResolve) {
        transaction.budgetSubCategoryName = transaction.calculatedCategory;
        let resolved: BudgetCategoryMapEntry;
        if (transaction.isInvestment) {
          const investmentEntry =
            amount < 0
              ? budgetMap.investmentSpending
              : budgetMap.investmentIncome;
          resolved =
            investmentEntry || resolveBudgetCategory(transaction, budgetMap);
        } else {
          resolved = resolveBudgetCategory(transaction, budgetMap);
        }
        transaction.calculatedCategory = resolved.name;
        transaction.budgetCategoryColor = resolved.color;
        transaction.budgetCategoryEmoji = resolved.emoji;
      }
    }
  }

  applyDuplicateGroupFlags(transactions, transactionRules);
}

export { transactionMatchesRule, orderRulesForApplication };

export function transactionHasRuleOfType(
  transaction: Transaction,
  actionType: string
): boolean {
  return (transaction.rules ?? []).some((r) => r.action_type === actionType);
}

export function getTransactionRuleOfType(
  transaction: Transaction,
  actionType: string
): TransactionRule | undefined {
  return (transaction.rules ?? []).find((r) => r.action_type === actionType);
}

/** Fills `transactionDescription` from OF `additionalInfo` / `details` (same as cashflow list). */
export function addTransactionDescription(transaction: Transaction) {
  if (transaction.description?.additionalInfo) {
    let transactionDescription = transaction.description.additionalInfo;
    transaction.transactionDescription = transactionDescription;

    try {
      let additionalInfo = JSON.parse(transaction.description.additionalInfo);
      transactionDescription = additionalInfo?.purposeDescription;
      transaction.transactionDescription = transactionDescription;
    } catch (e) {}
  } else if (transaction.details && !transaction.details.includes("{")) {
    transaction.transactionDescription = transaction.details;
  }
}

const INCOME_SUBCATEGORY_OVERRIDES = new Set([
  "SALARY",
  "SALARY_OTHER",
  "RESERVE_DUTY",
]);

const MAIN_CATEGORY_FALLBACKS_TO_SUB = new Set(["INCOMES_EXPENSES"]);

function shouldKeepSubCategory(main: string, sub: string) {
  return (
    MAIN_CATEGORY_FALLBACKS_TO_SUB.has(main) ||
    INCOME_SUBCATEGORY_OVERRIDES.has(sub)
  );
}

function applyRuleToTransaction(
  lang: Language,
  rule: TransactionRule,
  transaction: Transaction
) {
  if (!transaction.rules) transaction.rules = [];
  if (rule.action_type === transacitonActionType.IGNORE) {
    transaction.ignored = true;
  }
  if (rule.action_type === transacitonActionType.CATEGORY_CHANGE) {
    const nextCategory = canonicalizeCategoryChangeValue(rule.action_value);
    transaction.calculatedCategory =
      nextCategory || transaction.calculatedCategory;
  }
  if (rule.action_type === transacitonActionType.EDIT_TRANSACTION) {
    applyTransactionEditPayload(
      transaction,
      parseTransactionEditRulePayload(rule.action_value)
    );
  }
  if (rule.action_type === transacitonActionType.MARK_AS_INVESTMENT) {
    transaction.isInvestment = true;
    if (
      (!transaction.calculatedCategory &&
        transaction.category.main === "UNCATEGORIZED") ||
      transaction.calculatedCategory ===
        cashflowCharts[lang].Categories.Uncategorized ||
      transaction.calculatedCategory === "UNCATEGORIZED"
    ) {
      transaction.calculatedCategory =
        cashflowCharts[lang].Categories.InvestmentsOrSavins;
    }
  }
  if (!transaction.rules.some((r) => r.id === rule.id)) {
    transaction.rules.push(rule);
  }
}

function calculateTransactionsCategory(
  lang: Language,
  transaction: Transaction,
  expensesCategoryType: string,
  incomeCategoryType: string
) {
  const sub = transaction.changedCategory?.sub || transaction.category.sub;
  const main = transaction.changedCategory?.main || transaction.category.main;
  if (parseFloat(transaction.amount.chargedAmount.amount) < 0) {
    if (
      shouldKeepSubCategory(main, sub) ||
      expensesCategoryType === cashflowCharts[lang].CategoryTypes.Sub
    ) {
      transaction.calculatedCategory = sub;
    } else {
      transaction.calculatedCategory = main;
    }
  } else {
    if (
      shouldKeepSubCategory(main, sub) ||
      incomeCategoryType === cashflowCharts[lang].CategoryTypes.Sub
    ) {
      transaction.calculatedCategory = sub;
    } else {
      transaction.calculatedCategory = main;
    }
  }
}

export interface OptionInterface {
  value: string;
  label: string;
  parent_id: string;
}

export interface GroupedOption {
  label: string;
  options: OptionInterface[];
}

function getManualTransactionSource(
  transaction: Transaction
): ManualTransactionSource | null {
  if (!transaction.accountNumber?.startsWith(MANUAL_ACCOUNT_PREFIX)) {
    return null;
  }

  const source = transaction.accountNumber.replace(MANUAL_ACCOUNT_PREFIX, "");
  if (source === "cash" || source === "credit" || source === "bank") {
    return source;
  }

  return null;
}

function getManualAccountMetadata(
  lang: Language,
  source: ManualTransactionSource
) {
  switch (source) {
    case "credit":
      return {
        icon: "💳",
        label: cashflowCharts[lang].ManualCredit,
        parentId: cashflowCharts[lang].Card,
      };
    case "bank":
      return {
        icon: "🏦",
        label: cashflowCharts[lang].ManualBank,
        parentId: cashflowCharts[lang].Bank,
      };
    case "cash":
    default:
      return {
        icon: "💰",
        label: cashflowCharts[lang].Cash,
        parentId: cashflowCharts[lang].Manual,
      };
  }
}

function addUniqueAccountOption(
  group: OptionInterface[],
  option: OptionInterface
) {
  if (!group.some((existing) => existing.value === option.value)) {
    group.push(option);
  }
}

export function generateTransactionGroups(
  lang: Language,
  transactions: Transaction[]
): GroupedOption[] {
  let transactionGroups: GroupedOption[] = [];
  let checkingGroup: OptionInterface[] = [];
  let cardGroup: OptionInterface[] = [];
  let manualGroup: OptionInterface[] = [];
  for (const transaction of transactions) {
    const manualSource = getManualTransactionSource(transaction);
    if (manualSource) {
      const transactionGroup = generateTransactionGroup(lang, transaction);
      if (manualSource === "credit") {
        addUniqueAccountOption(cardGroup, transactionGroup);
      } else if (manualSource === "bank") {
        addUniqueAccountOption(checkingGroup, transactionGroup);
      } else {
        addUniqueAccountOption(manualGroup, transactionGroup);
      }
    } else if (transaction.type === "CARD" || transaction.type === "CHECKING") {
      let transactionGroup = generateTransactionGroup(lang, transaction);
      //check if transaction group already exists by comparing the label
      let existingTransactionGroup =
        transaction.type === "CARD"
          ? cardGroup.find(
              (existingGroup) => existingGroup.label === transactionGroup.label
            )
          : checkingGroup.find(
              (existingGroup) => existingGroup.label === transactionGroup.label
            );
      if (!existingTransactionGroup) {
        transaction.type === "CARD"
          ? cardGroup.push(transactionGroup)
          : checkingGroup.push(transactionGroup);
      }
    }
  }
  if (checkingGroup.length > 0) {
    transactionGroups.push({
      label: cashflowCharts[lang].Bank,
      options: checkingGroup,
    });
  }
  if (cardGroup.length > 0) {
    transactionGroups.push({
      label: cashflowCharts[lang].Card,
      options: cardGroup,
    });
  }
  if (manualGroup.length > 0) {
    transactionGroups.push({
      label: cashflowCharts[lang].Manual,
      options: manualGroup,
    });
  }
  return transactionGroups;
}

export function generateTransactionGroup(
  lang: Language,
  transaction: Transaction
): OptionInterface {
  const manualSource = getManualTransactionSource(transaction);
  if (manualSource) {
    const meta = getManualAccountMetadata(lang, manualSource);
    return {
      label: `${meta.icon} ${meta.label}`,
      value: transaction.accountNumber,
      parent_id: meta.parentId,
    };
  }

  let providerName = openFinanceProviderToNativeLanguageName(
    transaction.providerId
  );
  let accountNumber = generateShortAccountNumber(transaction.accountNumber);
  let type = transaction.type;
  let typeString = "";
  let ParentId = "";
  if (type === "CARD") {
    typeString = "💳";
    ParentId = cashflowCharts[lang].Card;
  } else if (type === "CHECKING") {
    typeString = "🏦";
    ParentId = cashflowCharts[lang].Bank;
  }
  let fullTransactionGroup = `${typeString} ${providerName} ${accountNumber} `;
  return {
    label: fullTransactionGroup,
    value: transaction.accountNumber,
    parent_id: ParentId,
  };
}

export function generateShortAccountNumber(accountNumber: string): string {
  if (!accountNumber) {
    return "";
  }
  return accountNumber.slice(-4).replace(/^0+/, "");
}

/**
 * Charged amount in ILS from raw transaction fields (currency conversion applied).
 * Ignored and auto-duplicate rows still contribute their magnitude here — use for list sort by sum.
 * Totals and charts use {@link getChargedAmmountInILS}, which treats those as zero.
 */
export function getChargedAmmountInILSForSort(
  transaction: Transaction,
  fiat: Fiat[],
  fiatHistory?: FiatRateHistory
): number {
  const localCurrency = fiat?.find((currency) => currency.value === "ILS");
  if (!localCurrency) {
    return 0;
  }
  let chargedAmount = parseFloat(transaction.amount.chargedAmount.amount);
  if (transaction.amount.chargedAmount.currency !== "ILS") {
    const currency = fiat.find(
      (c) => c.value === transaction.amount.chargedAmount.currency
    );
    if (currency) {
      // When fiat history is available, convert at the transaction date;
      // otherwise fall back to the current spot rate.
      const date = fiatHistory
        ? getDateFromTransacitonDate(transaction)
        : undefined;
      chargedAmount = convertCurrencyOnDate(
        chargedAmount,
        localCurrency,
        currency,
        date,
        fiatHistory
      );
    }
  }
  return chargedAmount || 0;
}

export function getChargedAmmountInILSIncludingInvestments(
  transaction: Transaction,
  fiat: Fiat[],
  fiatHistory?: FiatRateHistory
): number {
  if (transaction.ignored || transaction.autoDuplicate) {
    return 0;
  }
  return getChargedAmmountInILSForSort(transaction, fiat, fiatHistory);
}

export function getChargedAmmountInILS(
  transaction: Transaction,
  fiat: Fiat[],
  fiatHistory?: FiatRateHistory
): number {
  if (transaction.isInvestment) {
    return 0;
  }
  return getChargedAmmountInILSIncludingInvestments(
    transaction,
    fiat,
    fiatHistory
  );
}
